import api from '../lib/api';
import { toArray } from '../lib/utils';
import type { Promotion, PromotionCompany, Coupon, Company, Participant } from '../types/promotion';

class PromotionService {
  // Promotions
  async getPromotions(searchTerm?: string): Promise<Promotion[]> {
    const response = await api.get('/ace/promotions');
    const data = toArray<Promotion>(response.data);

    if (!searchTerm) return data;

    return data.filter((promotion: Promotion) =>
      promotion.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }

  async createPromotion(data: Partial<Promotion>): Promise<Promotion> {
    const response = await api.post('/ace/promotions', data);
    return response.data;
  }

  async updatePromotion(id: number, data: Partial<Promotion>): Promise<Promotion> {
    const response = await api.put(`/ace/promotions/${id}`, data);
    return response.data;
  }

  async deletePromotion(id: number): Promise<void> {
    await api.delete(`/ace/promotions/${id}`);
  }

  // Companies
  async getCompanies(): Promise<Company[]> {
    const response = await api.get('/ace/companies/all');
    return toArray(response.data);
  }

  async getPromotionCompanies(promotionId: number): Promise<PromotionCompany[]> {
    const response = await api.get('/ace/promotion-companies', {
      params: { promotion_id: promotionId },
    });
    return toArray(response.data);
  }

  async addCompanyToPromotion(data: Partial<PromotionCompany>): Promise<PromotionCompany> {
    const response = await api.post('/ace/promotion-companies', data);
    return response.data;
  }

  async updatePromotionCompany(
    id: number,
    data: Partial<PromotionCompany>
  ): Promise<PromotionCompany> {
    const response = await api.put(`/ace/promotion-companies/${id}`, data);
    return response.data;
  }

  async removeCompanyFromPromotion(id: number): Promise<void> {
    await api.delete(`/ace/promotion-companies/${id}`);
  }

  // Participants
  async getParticipants(): Promise<Participant[]> {
    const response = await api.get('/ace/participants');
    return toArray(response.data);
  }

  // Coupons
  async getCoupons(promotionCompanyId: number): Promise<Coupon[]> {
    const response = await api.get('/ace/coupons', {
      params: { promotion_company_id: promotionCompanyId },
    });
    return toArray(response.data);
  }

  async addCoupon(data: Partial<Coupon>): Promise<Coupon> {
    const response = await api.post('/ace/coupons', data);
    return response.data;
  }

  async deleteCoupon(id: number): Promise<void> {
    await api.delete(`/ace/coupons/${id}`);
  }
}

export default new PromotionService();
